// SessionManager.jsx
// Handles session warning (25 min) and session expiry (30 min)
import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Timing constants (Fixed Math) ────────────────────────────────────────
const WARNING_TIME    = 25 * 60 * 1000;
const EXPIRY_TIME     = 30 * 60 * 1000;
const COUNTDOWN_START = 5 * 60;
// ──────────────────────────────────────────────────────────────────────────

const EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'click'];

const getBackendUrl = () => {
  const h = window.location.hostname;
  if (h.includes('manurevasolutions.com') || process.env.NODE_ENV === 'production')
    return '';
  return 'http://localhost:5000';
};

const serverLogout = async () => {
  try {
    await (window._baseFetch || window.fetch)(`${getBackendUrl()}/auth/logout`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' }
    });
  } catch (e) {
    console.warn('Logout call failed', e);
  }
};

export default function SessionManager() {
  const [showWarning, setShowWarning] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [countdown,   setCountdown]   = useState(COUNTDOWN_START);
  const [extending,   setExtending]   = useState(false);

  const warningTimer = useRef(null);
  const expiryTimer  = useRef(null);
  const countdownInt = useRef(null);
  const isLoggedIn   = useRef(false);

  const clearAllTimers = useCallback(() => {
    clearTimeout(warningTimer.current);
    clearTimeout(expiryTimer.current);
    clearInterval(countdownInt.current);
  }, []);

  const saveReturnPath = () => {
    const path = window.location.pathname;
    if (path && path !== '/') {
      sessionStorage.setItem('returnTo', path);
    }
  };

  const startTimers = useCallback(() => {
    clearAllTimers();

    // 25 min → show warning modal with 5 min countdown
    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(COUNTDOWN_START);
      countdownInt.current = setInterval(() => {
        setCountdown(p => {
          if (p <= 1) { clearInterval(countdownInt.current); return 0; }
          return p - 1;
        });
      }, 1000);
    }, WARNING_TIME);

    // 30 min → show expired modal
    expiryTimer.current = setTimeout(() => {
      clearAllTimers();
      saveReturnPath();
      const returnTo = window.location.pathname;
      sessionStorage.clear();
      sessionStorage.setItem('returnTo', returnTo);
      isLoggedIn.current = false;
      setShowWarning(false);
      setShowExpired(true);
      serverLogout();
    }, EXPIRY_TIME);
  }, [clearAllTimers]);

  const resetTimers = useCallback(() => {
    if (!isLoggedIn.current || showExpired) return;
    setShowWarning(false);
    clearAllTimers();
    startTimers();
  }, [showExpired, clearAllTimers, startTimers]);

  // Init: check auth on mount (Safe execution)
  useEffect(() => {
    let mounted = true;
    fetch(`${getBackendUrl()}/api/user`, {
      credentials: 'include',
      headers: { Accept: 'application/json' }
    }).then(r => {
      if (!mounted) return;
      if (r.ok) { 
        isLoggedIn.current = true; 
        startTimers(); 
      }
    }).catch(() => {});
    
    return () => { 
      mounted = false; 
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  // Activity listeners — reset timers on any user interaction
  useEffect(() => {
    EVENTS.forEach(e => document.addEventListener(e, resetTimers, { passive: true }));
    return () => EVENTS.forEach(e => document.removeEventListener(e, resetTimers));
  }, [resetTimers]);

  // Global fetch interceptor — catches 401 from ANY page
  useEffect(() => {
    const original = window._baseFetch || window.fetch;
    window._baseFetch = original;

    window.fetch = async function (...args) {
      const res = await original.apply(this, args);
      if (res.status === 401 && window.location.pathname !== '/') {
        clearAllTimers();
        isLoggedIn.current = false;
        saveReturnPath();
        sessionStorage.clear();
        sessionStorage.setItem('returnTo', window.location.pathname);
        setShowWarning(false);
        setShowExpired(true);
      }
      return res;
    };

    return () => {
      if (window._baseFetch) window.fetch = window._baseFetch;
    };
  }, [clearAllTimers]);

  // ── Stay Logged In: refresh session silently, restart timers ─────────
  const handleStayLoggedIn = async () => {
    setExtending(true);
    try {
      const r = await window._baseFetch(`${getBackendUrl()}/api/user`, {
        credentials: 'include', headers: { Accept: 'application/json' }
      });
      if (r.ok) {
        setShowWarning(false);
        clearAllTimers();
        startTimers();
      } else {
        setShowWarning(false);
        setShowExpired(true);
      }
    } catch {
      setShowWarning(false);
      setShowExpired(true);
    }
    setExtending(false);
  };

  // ── Log In Again: checks cookie lifecycle chronologically ──────
  const handleLoginAgain = async () => {
  await serverLogout();
  const returnTo = sessionStorage.getItem('returnTo') || '/Admindashboard';
  sessionStorage.clear();
  sessionStorage.setItem('returnTo', returnTo);
  window.location.href = '/';
};

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (!showWarning && !showExpired) return null;

  // ── UI Styles ─────────────────────────────────────────────────────────
  const overlay = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(3px)',
    zIndex: 99998,
  };
  const center = {
    position: 'fixed', inset: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 99999,
  };
  const card = {
    background: '#fff', borderRadius: 16,
    padding: '40px 32px', width: '100%', maxWidth: 420,
    boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
    textAlign: 'center',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  };
  const iconBox = (bg) => ({
    width: 68, height: 68, borderRadius: '50%',
    background: bg, fontSize: 30,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
  });
  const h2style = { margin: '0 0 8px', fontSize: 21, fontWeight: 700, color: '#111' };
  const pstyle  = { margin: '0 0 24px', color: '#666', fontSize: 14, lineHeight: 1.6 };
  const btnGreen = (disabled) => ({
    flex: 1, padding: '12px 0',
    background: disabled ? '#9ca3af' : '#10b981',
    color: '#fff', border: 'none', borderRadius: 8,
    fontSize: 14, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  });
  const btnGray = {
    flex: 1, padding: '12px 0',
    background: '#f3f4f6', color: '#374151',
    border: '1px solid #e5e7eb', borderRadius: 8,
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
  };

  return (
    <>
      <div style={overlay} />

      {/* ── Warning Modal: appears at 25 min idle ─────────────────── */}
      {showWarning && (
        <div style={center}>
          <div style={card}>
            <div style={iconBox('#FEF3C7')}>⏰</div>
            <h2 style={h2style}>Session About to Expire</h2>
            <p style={pstyle}>
              You've been inactive for 25 minutes.<br />
              You'll be logged out in:
            </p>
            <div style={{
              fontSize: 44, fontWeight: 800,
              color: countdown < 60 ? '#ef4444' : '#f59e0b',
              letterSpacing: 3, margin: '0 0 28px',
            }}>
              {fmt(countdown)}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button style={btnGreen(extending)} onClick={handleStayLoggedIn} disabled={extending}>
                {extending ? 'Extending...' : '✓ Stay Logged In'}
              </button>
              {/* <button style={btnGray} onClick={handleLogout}>
                Log Out Now
              </button> */}
            </div>
          </div>
        </div>
      )}

      {/* ── Expired Modal: appears at 30 min idle or on 401 ──────── */}
      {showExpired && (
        <div style={center}>
          <div style={card}>
            <div style={iconBox('#FEE2E2')}>🔒</div>
            <h2 style={h2style}>Session Expired</h2>
            <p style={pstyle}>
              You've been logged out due to inactivity.<br />
              Click below to log in and continue<br />
              where you left off.
            </p>
            <button
              onClick={handleLoginAgain}
              style={{
                width: '100%', padding: '13px 0',
                background: '#10b981', color: '#fff',
                border: 'none', borderRadius: 8,
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
              }}>
              Log In Again
            </button>
          </div>
        </div>
      )}
    </>
  );
}